import React, { useState, useEffect } from 'react'
import LinearProgress from '@material-ui/core/LinearProgress'
import { fetchToken } from './token'
import useAuth from './useAuth'
import { AUTH_ROOT } from 'env'
import { roles } from 'lib/auth/constants'
import { gtmEvent } from 'lib/gtm'
import Redirect from 'components/redirect'
import IneligibleUserPage from 'pages/ineligible-user'
import castArray from 'lodash/castArray'
import intersection from 'lodash/intersection'
import Unauthorized from 'components/unauthorized'

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

const coachAuthRedirectRoles = [
  roles.COACH,
  roles.ADMIN,
  roles.PEC,
  roles.SECTION_STAFF,
  roles.JUNIOR_LEAGUE_COACH,
]

const consumerAuthRedirectRoles = [roles.CONSUMER]

function withProtectedRoute(Page, authorizedRoles = roles.NONE, redirectUrl) {
  authorizedRoles = castArray(authorizedRoles)

  function WithProtectedRoute(props) {
    const auth = useAuth()
    const [authReady, setAuthReady] = useState(false)
    const [gtmEventTriggered, setGtmEventTriggered] = useState(false) // manage state for gtm eventcallback

    useEffect(() => {
      const login = async () => {
        const authToken = await fetchToken()
        await auth.login(authToken, () => setAuthReady(true))
      }
      if (!authReady && !auth.isLoggedIn) {
        login()
      }
      const intervalId = setInterval(
        auth.refreshToken,
        TOKEN_REFRESH_INTERVAL_MS,
      )
      return () => {
        clearInterval(intervalId)
      }
    }, [auth, authReady])

    if (!authReady && !auth.isLoggedIn) {
      return <LinearProgress color="secondary" />
    }

    if (!auth.isLoggedIn && redirectUrl) {
      if (!gtmEventTriggered) {
        gtmEvent({
          event: 'unauthenticated-request',
          clickCategory: 'referrer-tracking',
          clickAction: 'redirect',
          clickLabel: 'unauthenticated-protected-route-request',
          eventCallback: () => setGtmEventTriggered(true),
          eventTimeout: 2000,
        })
      } else {
        return <Redirect to={redirectUrl} />
      }
    }

    if (
      auth.isLoggedIn &&
      authorizedRoles.includes(roles.COACH) &&
      auth.isCoach &&
      !auth.user.pgaCoachEligible
    ) {
      return <IneligibleUserPage />
    }

    if (auth.isLoggedIn && auth.user.hasRole(authorizedRoles)) {
      return <Page {...props} />
    }

    if (auth.isLoggedIn && redirectUrl) {
      return <Redirect to={redirectUrl} />
    }

    let loginRedirectUrl = `${AUTH_ROOT}/login?return_to=${encodeURIComponent(
      window.location.href,
    )}`

    // handle a user who tries access to access consumer-oriented pages when being logged in as a PGA member.
    if (
      auth.isLoggedIn &&
      auth.user.hasRole(coachAuthRedirectRoles) &&
      intersection(authorizedRoles, consumerAuthRedirectRoles).length > 0
    ) {
      return <Redirect to={loginRedirectUrl} />
    }

    // redirect routes restricted for only coaches directly to coach login flow
    if (intersection(authorizedRoles, coachAuthRedirectRoles).length > 0) {
      loginRedirectUrl = `${AUTH_ROOT}/v2/login?return_to=${encodeURIComponent(
        window.location.href,
      )}`
    }

    if (auth.isLoggedIn) return <Unauthorized />

    if (!gtmEventTriggered) {
      gtmEvent({
        event: 'unauthenticated-request',
        clickCategory: 'referrer-tracking',
        clickAction: 'redirect',
        clickLabel: 'unauthenticated-protected-route-request',
        eventCallback: () => setGtmEventTriggered(true),
        eventTimeout: 2000,
      })
    } else {
      return <Redirect to={loginRedirectUrl} />
    }

    return <LinearProgress color="secondary" />
  }

  return WithProtectedRoute
}

export default withProtectedRoute
